/**
 * <lp-nav-drawer> — a self-contained slide-in drawer: a trigger, a backdrop
 * and a panel. No framework, no imports, no dependency on Horizon's own
 * theme-drawer.js/focus.js/utilities.js (read for reference while building
 * this, not depended on).
 *
 * Drawer mechanics only. This component does not know or care that today's
 * panel happens to hold navigation links — it never queries for anything
 * more specific than "the trigger", "the backdrop", "the panel" and
 * "whatever is focusable inside the panel". Phase 3's filter drawer copies
 * this file (its own custom element, its own class-name prefix) rather than
 * importing it, so keeping the logic generic here is what makes that copy
 * a copy-and-rename instead of a rewrite.
 *
 * Markup contract, see sections/lp-header.liquid:
 *   <lp-nav-drawer>
 *     <button class="lp-nav-drawer__trigger" aria-controls="[panel id]">…</button>
 *     <div class="lp-nav-drawer__backdrop"></div>
 *     <div class="lp-nav-drawer__panel" id="[panel id]">
 *       <button class="lp-nav-drawer__close">…</button>   (optional)
 *       …focusable content…
 *     </div>
 *   </lp-nav-drawer>
 *
 * Open/closed state lives on the host as the `open` attribute. CSS reads it
 * (`.lp-nav-drawer[open] .lp-nav-drawer__panel { transform: … }`) to drive
 * the slide transition — this file never touches transform, opacity or
 * transition-duration directly, so it has no dependency on how long that
 * transition is. A reduced-motion visitor gets a transition-duration of
 * 0.01ms from the site-wide `@media (prefers-reduced-motion: reduce)` rule
 * in assets/lp-tokens.css (universal selector, !important — it wins the
 * cascade over this component's own transition declarations regardless of
 * selector specificity, because importance is resolved before specificity).
 * Nothing in this file reads or waits on that duration either: focus moves
 * and attributes flip synchronously on open()/close(), never gated behind a
 * transitionend/animationend event or a matching setTimeout, so there is no
 * separate reduced-motion branch to get out of sync with the CSS.
 */

/**
 * Elements a keyboard user can land on. Deliberately narrow (links, form
 * controls, explicit tabindex) rather than Horizon's own broader list
 * (assets/focus.js also includes summary, [draggable], area, iframe,
 * object) — narrow enough for both this task's nav links and phase 3's
 * filter controls (checkboxes, a price input, an apply button), without
 * pulling in element types neither drawer will ever contain.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

class LpNavDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLElement | null} */
  #closeButton = null;

  /** Element to return focus to on close — normally the trigger, but
   *  captured fresh on every open() rather than assumed, in case something
   *  else had focus when open() was called programmatically. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /**
   * Background elements this instance made inert, with enough of their
   * prior state to undo exactly that and nothing else — restoring a sibling
   * that already carried its own legitimate aria-hidden="true" must not
   * strip it back off on close just because this drawer also set it.
   * @type {{ el: Element, hadAriaHidden: boolean, priorAriaHidden: string | null }[]}
   */
  #inerted = [];

  /** Cached so close() restores the exact prior inline value rather than
   *  assuming it was empty (defensive against another script having already
   *  set body overflow for an unrelated reason). */
  /** @type {string} */
  #previousBodyOverflow = '';

  /**
   * The CSS in sections/lp-header.liquid hides .lp-nav-drawer entirely at
   * 1420px and up (display: none on the host removes the fixed-position
   * panel and backdrop from rendering too, regardless of their own
   * position). display: none does not close the drawer, though — if a
   * visitor resizes or rotates past 1420px while it is open, the panel just
   * disappears while the `open` attribute, the background inert lock, the
   * scroll lock and the document-level key/focus listeners all stay live,
   * leaving the rest of the page permanently unfocusable with no visible
   * drawer to explain why. This listener closes the drawer the moment the
   * breakpoint is crossed so none of that state outlives the viewport
   * change.
   *
   * Must match the header's handoff breakpoint. It stayed at 750 through the moves to 990 and
   * 1290, so between 750 and the handoff a resize with the drawer open left the page locked.
   */
  #desktopQuery = window.matchMedia('(min-width: 1420px)');

  connectedCallback() {
    this.#trigger = this.querySelector('.lp-nav-drawer__trigger');
    this.#backdrop = this.querySelector('.lp-nav-drawer__backdrop');
    this.#panel = this.querySelector('.lp-nav-drawer__panel');
    this.#closeButton = this.querySelector('.lp-nav-drawer__close');

    if (!this.#trigger || !this.#panel) {
      // Malformed markup — nothing safe to wire up without a trigger and a
      // panel to trap focus inside.
      return;
    }

    // A valid focus() target even when the panel has no focusable content
    // of its own — see #focusFirst.
    this.#panel.tabIndex = -1;

    if (!this.hasAttribute('open')) {
      this.#panel.setAttribute('inert', '');
    }

    this.#trigger.addEventListener('click', this.#onTriggerClick);
    this.#backdrop?.addEventListener('click', this.#onBackdropClick);
    this.#closeButton?.addEventListener('click', this.#onCloseClick);
    this.#desktopQuery.addEventListener('change', this.#onDesktopBreakpointChange);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.#backdrop?.removeEventListener('click', this.#onBackdropClick);
    this.#closeButton?.removeEventListener('click', this.#onCloseClick);
    this.#desktopQuery.removeEventListener('change', this.#onDesktopBreakpointChange);
    this.#removeDocumentListeners();
    this.#unlockBackground();
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.hasAttribute('open');
  }

  #onTriggerClick = () => {
    this.toggle();
  };

  #onBackdropClick = () => {
    this.close();
  };

  #onCloseClick = () => {
    this.close();
  };

  /**
   * @param {MediaQueryListEvent} event
   */
  #onDesktopBreakpointChange = (event) => {
    if (event.matches && this.isOpen) {
      this.close();
    }
  };

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    if (!this.#trigger || !this.#panel) return;

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    this.setAttribute('open', '');
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#panel.removeAttribute('inert');
    this.#panel.removeAttribute('aria-hidden');

    this.#lockBackground();
    this.#lockScroll();

    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('focusin', this.#onFocusIn, true);

    // The close button, when present, is the first focusable element in the
    // panel by markup order (sections/lp-header.liquid puts it before the
    // nav list) — so "focus the first focusable thing in the panel" and
    // "focus the close button first" are the same instruction here. Generic
    // by construction: this file never names .lp-nav-drawer__close, so a
    // panel with no close button (or one placed elsewhere) still gets
    // sensible initial focus.
    this.#focusFirst();
  }

  close() {
    if (!this.isOpen) return;
    if (!this.#trigger || !this.#panel) return;

    this.removeAttribute('open');
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#panel.setAttribute('aria-hidden', 'true');
    this.#panel.setAttribute('inert', '');

    this.#unlockBackground();
    this.#unlockScroll();
    this.#removeDocumentListeners();

    const target = this.#previouslyFocused;
    this.#previouslyFocused = null;

    // target !== document.body: macOS Safari does not move focus to a
    // <button> on click (a long-standing platform quirk, unrelated to
    // anything in this file), so document.activeElement at open() time is
    // still <body> for a mouse-opened drawer there — captured verbatim,
    // that would make close() "restore" focus to <body> instead of back to
    // the trigger, silently failing the return-focus-to-trigger requirement
    // on that one browser/input combination. Treating body as "nothing
    // meaningful was focused" and falling through to the trigger covers it.
    //
    // document.contains(target): guards against the trigger having been
    // replaced (e.g. a Section Rendering API re-render swapped the node)
    // between open() and close() — .focus() on a detached node is a silent
    // no-op, so fall back to the live trigger reference instead of losing
    // focus to the document body.
    if (target && target !== document.body && document.contains(target)) {
      target.focus();
    } else {
      this.#trigger.focus();
    }
  }

  #removeDocumentListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('focusin', this.#onFocusIn, true);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      // isComposing: don't close while an IME (Japanese/Chinese/Korean
      // input) is consuming Escape to dismiss its own candidate list.
      // defaultPrevented: let anything nested inside the panel handle
      // Escape first (e.g. a future combobox in a phase 3 filter control).
      if (event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.#trapTab(event);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #trapTab(event) {
    if (!this.#panel) return;

    const focusable = this.#getFocusableElements();

    if (focusable.length === 0) {
      // Nothing to cycle between — pin focus to the panel itself (its own
      // tabindex="-1") rather than letting Tab fall through to whatever is
      // behind it.
      event.preventDefault();
      this.#panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !this.#panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !this.#panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Capture-phase safety net alongside the Tab handler above (the same
   * belt-and-suspenders pairing Horizon's own assets/focus.js uses): if
   * focus lands outside the panel by any route the Tab interception doesn't
   * cover — a stray programmatic .focus() call from something else on the
   * page, for instance — pull it back in immediately rather than leaving it
   * to escape to the page behind.
   *
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (!this.#panel) return;
    const target = /** @type {Node | null} */ (event.target);
    if (target && !this.#panel.contains(target)) {
      this.#focusFirst();
    }
  };

  #focusFirst() {
    if (!this.#panel) return;
    const [first] = this.#getFocusableElements();
    (first ?? this.#panel).focus();
  }

  /**
   * Recomputed on every Tab press rather than cached once at open() —
   * content inside the panel can change after opening (a future accordion
   * in the drawer, a filter option that becomes unavailable), and a stale
   * list would let Tab land on something no longer visible or skip past
   * something that just became visible.
   *
   * Filtered by getClientRects().length, not the more common offsetParent
   * check: offsetParent is null for position: fixed elements — which this
   * panel is — so it would misreport every genuinely visible child as
   * hidden too. getClientRects().length is 0 for display:none or zero-size
   * elements and non-zero otherwise, regardless of positioning scheme.
   *
   * @returns {HTMLElement[]}
   */
  #getFocusableElements() {
    if (!this.#panel) return [];
    return Array.from(this.#panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el instanceof HTMLElement && el.getClientRects().length > 0
    );
  }

  /**
   * Marks everything outside the drawer's own ancestor chain inert, walking
   * from this element up to <body> one parent at a time and inert-ing every
   * sibling along the way. Generic on purpose: it never names "header",
   * "main" or any other landmark, so it locks out the same background
   * regardless of where in the document a drawer happens to be mounted —
   * phase 3's filter drawer, wherever it lives, gets the same background
   * lock for free.
   */
  #lockBackground() {
    /** @type {Element | null} */
    let node = this;

    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;

      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (sibling.hasAttribute('inert')) continue;

        this.#inerted.push({
          el: sibling,
          hadAriaHidden: sibling.hasAttribute('aria-hidden'),
          priorAriaHidden: sibling.getAttribute('aria-hidden'),
        });

        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      }

      node = parent;
    }
  }

  #unlockBackground() {
    for (const { el, hadAriaHidden, priorAriaHidden } of this.#inerted) {
      el.removeAttribute('inert');
      if (hadAriaHidden) {
        el.setAttribute('aria-hidden', /** @type {string} */ (priorAriaHidden));
      } else {
        el.removeAttribute('aria-hidden');
      }
    }
    this.#inerted = [];
  }

  /**
   * Independent of Horizon's own scroll-lock (assets/utilities.js'
   * lockScroll/unlockScroll, which ref-counts "owners" and toggles a
   * scroll-lock attribute on <html>) rather than participating in it — this
   * component has no import access to that module's private owner Set, and
   * driving it via a duplicated, unregistered "owner" would risk another
   * script's unlockScroll() call clearing the lock while this drawer is
   * still open. Setting document.body.style.overflow directly is a fully
   * separate mechanism on a different element, so the two cannot clobber
   * each other — worst case if both are ever active at once is a harmless
   * redundant lock, not a stuck-open or stuck-closed scrollbar.
   */
  #lockScroll() {
    this.#previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.body.style.overflow = this.#previousBodyOverflow;
  }
}

if (!customElements.get('lp-nav-drawer')) {
  customElements.define('lp-nav-drawer', LpNavDrawer);
}
